import { AxiosError } from 'axios';
import { SignUpTypes } from 'interfaces';
import {
  FirstLoginPayload,
  FirstLoginResponse,
  SubscribeApiErrors,
  SubscribeContext,
  SubscribeFormValues,
} from 'interfaces/subscribe.types';
import { useMutation } from 'react-query';
import { AuthAPI } from 'services/auth.api';
import { SubscribeAPI } from 'services/subscribe.api';

// ---------- Mapping helpers ----------

/**
 * Mappe les valeurs du formulaire vers le payload POST /auth/first-login.
 * On envoie tous les champs gérés par le back (FirstLoginSubmitAction),
 * en omettant ceux laissés vides par l'utilisateur. Le back ignorera
 * les champs non éditables / non configurés.
 */
function mapToFirstLoginPayload(
  values: SubscribeFormValues,
): FirstLoginPayload {
  const payload: FirstLoginPayload = {};

  if (values.gender !== '' && values.gender != null) {
    payload.gender = Number(values.gender);
  }
  if (values.lastName) payload.lastName = values.lastName;
  if (values.firstName) payload.firstName = values.firstName;
  if (values.email) payload.email = values.email.trim();
  if (values.clientId) payload.clientId = values.clientId;
  if (values.password) payload.password = values.password;
  if (values.phone) payload.phone = values.phone;
  if (values.companyName) payload.companyName = values.companyName;

  // Adresse
  if (values.addressName) payload.addressTitle = values.addressName;
  if (values.addressGender !== '' && values.addressGender != null) {
    payload.addressGender = Number(values.addressGender);
  }
  if (values.addressLastName) payload.addressLastName = values.addressLastName;
  if (values.addressFirstName)
    payload.addressFirstName = values.addressFirstName;
  if (values.addressCompanyName)
    payload.addressCompanyName = values.addressCompanyName;
  if (values.addressStreet) payload.addressLine1 = values.addressStreet;
  if (values.addressComplement) payload.addressLine2 = values.addressComplement;
  if (values.addressPostalCode)
    payload.addressZipCode = values.addressPostalCode;
  if (values.addressCity) payload.addressCity = values.addressCity;
  if (values.addressCountry) payload.addressCountry = values.addressCountry;

  // Consentements
  if (values.optin != null) payload.optin = values.optin ? 1 : 2;
  if (values.terms != null) payload.isCGUAccepted = values.terms;
  if (values.referralCode) payload.referralCode = values.referralCode;

  return payload;
}

/**
 * Mappe les valeurs du formulaire vers le payload POST /users/register.jsonld
 * (inscription autonome — route existante)
 */
function mapToSignUpPayload(values: SubscribeFormValues): SignUpTypes {
  return {
    gender: values.gender,
    lastName: values.lastName,
    firstName: values.firstName,
    companyName: values.companyName || undefined,
    username: values.email.trim(),
    email: values.email.trim(),
    clientId: values.clientId,
    password: values.password,
    optin: values.optin ? 1 : 2,
    isCGUAccepted: values.terms,
    referralCode: values.referralCode || undefined,
    phone: values.phone || undefined,
    addressName: values.addressName || undefined,
    addressGender: values.addressGender || undefined,
    addressLastName: values.addressLastName || undefined,
    addressFirstName: values.addressFirstName || undefined,
    addressCompanyName: values.addressCompanyName || undefined,
    addressStreet: values.addressStreet || undefined,
    addressComplement: values.addressComplement || undefined,
    addressPostalCode: values.addressPostalCode || undefined,
    addressCity: values.addressCity || undefined,
    addressCountry: values.addressCountry || undefined,
  };
}

// ---------- Hook ----------

type SubmitResult = {
  registered?: boolean;
  firstLoginResponse?: FirstLoginResponse;
};

/**
 * Hook de mutation pour soumettre le formulaire.
 * - inscription autonome → POST /users/register.jsonld (route existante)
 * - pré-inscription / first-login → POST /auth/first-login (nouvelle route)
 */
export function useSubmitSubscribeForm(context: SubscribeContext) {
  return useMutation<
    SubmitResult,
    AxiosError<SubscribeApiErrors>,
    SubscribeFormValues
  >(async (values: SubscribeFormValues) => {
    if (context === 'autonomous-registration') {
      const payload = mapToSignUpPayload(values);
      // Le back pose le JWT en cookie httpOnly (max_api_token) et renvoie
      // un corps vide : un 200 sans exception suffit à confirmer le succès.
      await AuthAPI.signUp(payload);
      return { registered: true } as SubmitResult;
    }
    // pre-registration ou first-login
    const payload = mapToFirstLoginPayload(values);
    const { data } = await SubscribeAPI.submitFirstLogin(payload);
    return { firstLoginResponse: data } as SubmitResult;
  });
}
